import { isLoggedIn } from "@workspace/shared/core/store/auth/auth.store";
import type { SupportVideo } from "./supportApi.ts";

export interface SupportMessage {
  role: "user" | "assistant";
  content: string;
  pending?: boolean;
  suggestions?: string[];
  unresolved?: boolean;
  video?: SupportVideo | null;
  /**
   * A client-side notice (a network failure), not a real answer from the
   * assistant. Never persisted, never replayed to the model, and never
   * attached to a ticket — otherwise ten offline attempts would evict the
   * coach's ten cached answers and feed the model its own error text.
   */
  transient?: boolean;
}

/** Every key Help and Support keeps in localStorage starts with this — and
 * signing out clears every one (AuthContext). */
export const SUPPORT_STORAGE_PREFIX = "mofo:practice-support";

// The practice apps' own history: their assistant answers from a different
// FAQ than the in-game one, so the two conversations are kept apart.
const STORAGE_KEY = `${SUPPORT_STORAGE_PREFIX}-chat`;

/** How many answers are kept between visits (client 2026-09). */
export const MAX_STORED_ANSWERS = 10;

/**
 * Trims to the last {@link MAX_STORED_ANSWERS} answers, keeping the question
 * that produced each one.
 *
 * Counting answers rather than messages is what keeps an exchange intact — a
 * flat "last 20 messages" cut can drop a question and leave its answer sitting
 * there with nothing to explain it.
 */
export const trimToRecentAnswers = (messages: SupportMessage[]): SupportMessage[] => {
  let answers = 0;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.role !== "assistant") continue;
    answers++;
    if (answers > MAX_STORED_ANSWERS) {
      // Everything from this answer back goes, INCLUDING it — cutting at
      // the answer itself would leave an eleventh one behind, and its
      // question with it.
      return messages.slice(i + 1);
    }
  }
  return messages;
};

/**
 * The chat is restored when the coach comes back.
 *
 * Support questions repeat — the same coach asks the same thing across a
 * season — and losing the answer the moment the panel closes means asking the
 * model (and paying for it) again. localStorage rather than the API: it is
 * this coach's own scratch history, worth nothing to anyone else, and it must
 * still be there with no internet.
 */
export const loadSupportHistory = (): SupportMessage[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return trimToRecentAnswers(
      parsed
        // A pending bubble is a live spinner; restoring one would show a
        // "Looking that up..." that never resolves.
        .filter(
          (m): m is SupportMessage =>
            m &&
            (m.role === "user" || m.role === "assistant") &&
            typeof m.content === "string" &&
            !m.pending
        )
        .map((m) => ({
          role: m.role,
          content: m.content,
          // Only what the chat can render: a malformed entry in storage must
          // not take the app down with it.
          suggestions: Array.isArray(m.suggestions)
            ? m.suggestions.filter((s: unknown): s is string => typeof s === "string")
            : undefined,
          unresolved: m.unresolved === true,
          video:
            m.video && typeof m.video.url === "string"
              ? {
                  url: m.video.url,
                  question: typeof m.video.question === "string" ? m.video.question : "",
                }
              : null,
        }))
    );
  } catch {
    return [];
  }
};

export const saveSupportHistory = (messages: SupportMessage[]): void => {
  // Signed out (an answer landing as the coach signs out): the sweep has run,
  // and saving now would hand this chat to the next coach on the iPad.
  if (!isLoggedIn()) return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(trimToRecentAnswers(messages.filter((m) => !m.pending && !m.transient)))
    );
  } catch {
    // A full or unavailable localStorage must not break the chat.
  }
};
